import { Button as BaseButton } from '@sohanscript/ui/button';
import type * as React from 'react';
import { Link, type LinkProps } from '@/components/link';

export type ButtonProps = React.ComponentProps<typeof BaseButton> &
	Pick<LinkProps, 'viewTransition' | 'target'>;

export function Button({
	href,
	noRef,
	viewTransition,
	target,
	...props
}: ButtonProps) {
	return (
		<BaseButton
			{...props}
			href={href}
			noRef={noRef}
			target={target}
			viewTransition={viewTransition}
			renderHref={({
				href: raw,
				label,
				noRef: linkNoRef,
				viewTransition: linkVT,
			}) => {
				// NOTE: internal routes keep SPA navigation + view transitions through
				// the router-aware `Link`; external `https://` links fall through to it
				// too so UTM params match the rest of the site.
				const isExternal = raw.startsWith('https://');
				const [beforeHash, hash] =
					!isExternal && raw.includes('#')
						? [raw.split('#')[0], raw.split('#')[1] as string | undefined]
						: [raw, undefined];
				const [path, query] = beforeHash.split('?');
				const to = isExternal
					? (raw as `https://${string}`)
					: ((path || '/') as '/');
				// NOTE: `?service=slug` preselects the /start dropdown.
				const search = query
					? Object.fromEntries(new URLSearchParams(query))
					: undefined;
				return (
					<Link
						to={to}
						hash={hash}
						search={search as never}
						target={target}
						className="absolute inset-0"
						noRef={linkNoRef}
						viewTransition={linkVT}
						aria-label={label}
					/>
				);
			}}
		/>
	);
}

export { buttonVariants } from '@sohanscript/ui/button-variants';
